import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import formatToISODate from "../formatDateISO";
import { Dialog } from "primereact/dialog";

import DevisDialog from "./DevisDialog/DevisDialog";
import { useDialogStyles } from './DevisDialog/styles';

import {
  formatDateWithDaysAgo,
  invitesTemplate,
  typeTemplate,
  nameTemplate,
} from "./formatTableau";

const apiUrl = process.env.REACT_APP_API_URL;

export default function AbandonsFiche({ searchTerm = "" }) {
  const [loading, setLoading] = useState(true);

  const [ficheLeadAbandons, setFicheLeadAbandons] = useState([]);

  const [copiedText, setCopiedText] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [openSnackbarMail, setOpenSnackbarMail] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [ficheListeFiltre, setFicheListeFiltre] = useState("");

  const [devisDialogVisible, setDevisDialogVisible] = useState(false);
  const [currentLeadForDevis, setCurrentLeadForDevis] = useState(null);

  const [forceNewDevis, setForceNewDevis] = useState(false);

  const [selectedLeads, setSelectedLeads] = useState([]);
  const [bulkActionDialog, setBulkActionDialog] = useState(false);
  const [bulkActionStatus, setBulkActionStatus] = useState("");

  const openDevisDialog = (leadData, isNew = false) => {
    setForceNewDevis(isNew);
    setCurrentLeadForDevis({
      idLead: leadData.idLead,
      nomPrenom: leadData.nomPrenom,
      email: leadData.email,
      tel: leadData.tel,
      lieu: leadData.lieu,
      type_bar: leadData.type_bar,
      dateEvent: leadData.dateFicheLead,
      nbpers: leadData.nbpers,
      plusInfos: leadData.more,
    });
    setDevisDialogVisible(true);
  };

  const handleDevisGenerated = () => {
    axios
      .get(`${apiUrl}/IHM/api/lead.php?ficheLeadAbandons`)
      .then((response) => {
        setFicheLeadAbandons(response.data);
      })
      .catch((error) => {
        console.error("Erreur lors du rechargement :", error);
      });
  };

  const copyToClipboard = (text) => {
    if (text !== "") {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            setCopiedText(text);
            setOpenSnackbar(true);
          })
          .catch((error) => {
            console.error(
              "Erreur lors de la copie dans le presse-papiers : ",
              error,
            );
          });
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        setCopiedText(text);
        setOpenSnackbar(true);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  const handleCloseSnackbarMail = () => {
    setOpenSnackbarMail(false);
  };

  useEffect(() => {
    axios
      .get(`${apiUrl}/IHM/api/lead.php?ficheLeadAbandons`)
      .then((response) => {
        setFicheLeadAbandons(response.data);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement des factures :", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const recherche = searchTerm.toLowerCase();
    const filteredList = ficheLeadAbandons.filter((fiche) => {
      const nomPrenom = fiche.nomPrenom?.toLowerCase() || "";
      const tel = fiche.tel?.toLowerCase() || "";
      const lieu = fiche.lieu?.toLowerCase() || "";
      const email = fiche.email?.toLowerCase() || "";
      return (
        nomPrenom.includes(recherche) ||
        tel.includes(recherche) ||
        lieu.includes(recherche) ||
        email.includes(recherche)
      );
    });
    setFicheListeFiltre(filteredList);
  }, [searchTerm, ficheLeadAbandons]);

   const handleBulkAction = async () => {
     if (selectedLeads.length === 0 || !bulkActionStatus) return;

     // Map des statuts vers les libellés
     const statusLabels = {
       1: "Nouvelles",
       2: "Relances",
       6: "Envoyés",
       4: "Réalisés",
       5: "Abandons",
     };

     const statusName = statusLabels[bulkActionStatus] || "inconnu";

     try {
       const promises = selectedLeads.map((lead) =>
         axios.put(`${apiUrl}/IHM/api/lead.php`, {
           idLead: lead.idLead,
           etat: parseInt(bulkActionStatus),
           adminMod: localStorage.getItem("us"),
           note: `Action groupée : passage à ${statusName}`,
           ...(parseInt(bulkActionStatus) === 3 && {
             dateValidation: formatToISODate(new Date()),
           }),
         }),
       );

       await Promise.all(promises);

       const response = await axios.get(
         `${apiUrl}/IHM/api/lead.php?ficheLeadAbandons`,
       );
       setFicheLeadAbandons(response.data);

       setSnackbarMessage(
         `${selectedLeads.length} lead(s) passé(s) à l'état "${statusName}"`,
       );
       setOpenSnackbarMail(true);
       setSelectedLeads([]);
       setBulkActionDialog(false);
       setBulkActionStatus("");
     } catch (error) {
       console.error("Erreur lors de l'action groupée :", error);
       setSnackbarMessage("Erreur lors de la mise à jour");
       setOpenSnackbarMail(true);
     }
   };

   const startToolbar = () => {
     if (selectedLeads.length === 0) return null;

     return (
       <div
         style={{
           display: "flex",
           justifyContent: "space-between",
           alignItems: "center",
           padding: "8px 0",
           width: "100%",
           gap: "12px",
           flexWrap: "wrap",
         }}
       >
         <span style={{ fontSize: "14px", fontWeight: "500" }}>
           {selectedLeads.length} sélectionné(s)
         </span>
         <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
           <select
             value={bulkActionStatus}
             onChange={(e) => {
               setBulkActionStatus(e.target.value);
               setBulkActionDialog(true);
             }}
             style={{
               height: "38px",
               padding: "0 12px",
               border: "1px solid #d1d5db",
               borderRadius: "6px",
               backgroundColor: "#ffffff",
               fontSize: "13px",
               color: "#322915",
               cursor: "pointer",
               minWidth: "180px",
             }}
           >
             <option value="">Changer le statut</option>
             <option value="1">Nouvelles</option>
             <option value="2">Relances</option>
             <option value="6">Envoyés</option>
             <option value="4">Réalisés</option>
             <option value="5">Abandons</option>
           </select>
         </div>
       </div>
     );
   };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={openSnackbarMail}
        autoHideDuration={1000}
        onClose={handleCloseSnackbarMail}
        message={snackbarMessage}
        action={
          <React.Fragment>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseSnackbarMail}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </React.Fragment>
        }
      />

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={1000}
        onClose={handleCloseSnackbar}
        message={`Copie réussie de " ${
          copiedText === "location.mail" ? "e-mail" : copiedText
        } "`}
        action={
          <React.Fragment>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseSnackbar}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </React.Fragment>
        }
      />

      <DevisDialog
        visible={devisDialogVisible}
        onHide={() => {
          setDevisDialogVisible(false);
          setForceNewDevis(false);
        }}
        leadData={currentLeadForDevis}
        onDevisGenerated={() => {
          handleDevisGenerated();
          setForceNewDevis(false);
        }}
        forceNew={forceNewDevis}
        onResetForceNew={() => setForceNewDevis(false)}

      />

      <Dialog
        visible={bulkActionDialog}
        header="Confirmation"
        modal
        style={{ width: "400px" }}
        onHide={() => {
          setBulkActionDialog(false);
          setBulkActionStatus("");
        }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Annuler"
              icon="pi pi-times"
              severity="secondary"
              outlined
              style={{ fontSize: "13px" }}
              className="btn-outlined-clean"
              onClick={() => {
                setBulkActionDialog(false);
                setBulkActionStatus("");
              }}
            />
            <Button
              label="Confirmer"
              icon="pi pi-check"
              style={{ fontSize: "13px" }}
              className="brand-btn-primary"
              onClick={handleBulkAction}
            />
          </div>
        }
      >
        <div className="p-3">
          <p>
            Passer les {selectedLeads.length} lead(s) à l'état :{" "}
            {{
              1: "NOUVELLES",
              2: "RELANCES",
              6: "ENVOYÉS",
              4: "RÉALISÉS",
              5: "ABANDONS",
            }[bulkActionStatus] || "INCONNU"}
          </p>
        </div>
      </Dialog>

      <DataTable
        value={ficheListeFiltre}
        paginator
        size="small"
        rows={100}
        rowsPerPageOptions={[100, 200]}
        tableStyle={{ minWidth: "50rem" }}
        scrollable
        scrollDirection="horizontal"
        paginatorTemplate={
          "FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
        }
        paginatorRight={
          <span style={{ margin: "10px", fontWeight: "bold" }}>
            Résultats : {ficheListeFiltre.length}
          </span>
        }
        loading={loading}
        loadingIcon="pi pi-spin pi-spinner"
        emptyMessage={
          loading
            ? "Chargement des nouvelles fiches..."
            : "Aucune fiche pour le moment"
        }
         selectionMode="multiple"
        selection={selectedLeads}
        onSelectionChange={(e) => {
          setSelectedLeads(e.value);
        }}
        dataKey="idLead"
        header={startToolbar()}
      >

        <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
        
        <Column
          field="dateUpdate"
          header="Date"
          body={(rowData) => formatDateWithDaysAgo(rowData.dateUpdate)}
        />

        <Column
          field="nom"
          header="Client"
          body={(rowData) => nameTemplate(rowData, copyToClipboard)}
        />

        <Column
          field="date"
          header="Date event"
          body={(rowData) => formatDateWithDaysAgo(rowData.dateFicheLead)}
        />

        <Column
          field="nbpers"
          header="Invités"
          body={(rowData) => invitesTemplate(rowData, copyToClipboard)}
        />

        <Column
          field="type_bar"
          header="Type"
          body={(rowData) => typeTemplate(rowData)}
        />

        <Column
          header="Actions"
          style={{ textAlign: "center", width: "100px" }}
          body={(rowData) => (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "5px",
              }}
            >
              {rowData.a_devis == 1 && (
                <>
                  <Button
                    icon="pi pi-file-pdf"
                    severity="info"
                    text
                    raised
                    size="small"
                    tooltip="Gérer le devis"
                    tooltipOptions={{
                      position: "top",
                      style: { fontSize: "0.7rem" },
                    }}
                    onClick={() => openDevisDialog(rowData, false)}
                  />
                </>
              )}
              <Button
                icon="pi pi-plus"
                severity="warning"
                text
                raised
                size="small"
                tooltip="Nouveau devis"
                tooltipOptions={{
                  position: "top",
                  style: { fontSize: "0.7rem" },
                }}
                onClick={() => openDevisDialog(rowData, true)}
              />
            </div>
          )}
        />
      </DataTable>
    </>
  );
}
